import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LayoutDashboard, Upload as UploadIcon } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';

const LINKS = [
  { to: '/guides', label: 'Guides', testid: 'nav-guides-link' },
  { to: '/pricing', label: 'Pricing', testid: 'nav-pricing-link' },
];

/** Public site header. Desktop keeps the inline links; phones get a proper menu
 *  instead of silently hiding Guides and Pricing. */
export const PublicNav = ({ isAuthenticated, current }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <nav className="glass-header fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="VerifAEye home">
            <Logo size="md" />
          </Link>

          <div className="hidden items-center gap-4 sm:flex">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`text-sm ${current === l.to ? 'font-semibold text-slate-900' : 'font-medium text-slate-600 hover:text-slate-900'}`}
              >
                {l.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" className="text-slate-600 hover:text-slate-900">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
                <Link to="/upload">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800">
                    <UploadIcon className="mr-2 h-4 w-4" />
                    Add Items
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login-btn" className="text-slate-600 hover:text-slate-900">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button data-testid="nav-register-btn" className="bg-slate-900 text-white hover:bg-slate-800">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile */}
          <div className="flex items-center gap-2 sm:hidden">
            {!isAuthenticated && (
              <Link to="/register">
                <Button size="sm" className="bg-slate-900 px-3 text-white hover:bg-slate-800" data-testid="nav-register-btn-mobile">
                  Start free
                </Button>
              </Link>
            )}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              className="-mr-2 flex h-12 w-12 items-center justify-center rounded-lg text-slate-700 active:bg-slate-100"
              data-testid="mobile-menu-btn"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="border-t border-slate-200 bg-white px-4 pb-6 pt-2 shadow-lg sm:hidden"
          data-testid="mobile-menu-panel"
        >
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="flex min-h-[52px] items-center border-b border-slate-100 text-base font-medium text-slate-800"
              data-testid={`${l.testid}-mobile`}
            >
              {l.label}
            </Link>
          ))}
          {isAuthenticated ? (
            <div className="mt-4 grid gap-2">
              <Link to="/dashboard" onClick={() => setOpen(false)}>
                <Button variant="outline" className="h-12 w-full border-slate-300">Dashboard</Button>
              </Link>
              <Link to="/upload" onClick={() => setOpen(false)}>
                <Button className="h-12 w-full bg-slate-900 text-white">Add Items</Button>
              </Link>
            </div>
          ) : (
            <div className="mt-4 grid gap-2">
              <Link to="/login" onClick={() => setOpen(false)}>
                <Button variant="outline" className="h-12 w-full border-slate-300" data-testid="nav-login-btn-mobile">
                  Sign In
                </Button>
              </Link>
              <Link to="/register" onClick={() => setOpen(false)}>
                <Button className="h-12 w-full bg-slate-900 text-white">Create free account</Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};

export default PublicNav;
